import React from 'react';
import 'react-toggle/style.css';
import styled from 'styled-components';
import { DASHBOARD_REFRESH_INTERVAL_MS } from 'src/config';
import IRhapsodyDashboard from 'src/ServerEntities/IRhapsodyDashboard';
import IRhapsodyServer from 'src/ServerEntities/IRhapsodyServer';
import ErrorBox from 'src/SharedComponents/ErrorBox';
import LoadingIndicator from 'src/SharedComponents/LoadingIndicator';
import { SessionContext } from 'src/Views/SessionContext';
import { getRhapsodyDashboard, getRhapsodyHeader } from '../ramService';
import useCookie from 'src/CustomHooks/useCookie';
import AlertsPanel from '../Components/AlertsPanel';
import MessageThroughputPanel from '../Components/MessageThroughputPanel';
import ServerDetails from '../Components/ServeDetailsPanel';
import { RolesContext } from 'src/Roles/RolesContext';
import DiskUsagePanel from '../Components/DiskUsagePanel';
import IntersystemsComponentPanel from '../Components/IntersystemsComponentPanel';
import ServerSelector from 'src/SharedComponents/Select/ServerSelector';
import { getExpiryDate } from '../Rhapsody/RhapdosyDashboard';

const SiteContainer = styled.ul`
    margin-top: 24px;
    flex: 1 1 auto;
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    justify-content: flex-start;
    align-items: flex-start;
`;

export const ContentContainer = styled.main`
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    @media (min-width: 1279px) {
        padding: 10px 48px 104px;
    }
    @media (max-width: 1279px) {
        margin-bottom: 120px;
    }
`;

interface IProps {
    selectedTypes: string[],
    changeRole: boolean,
    setChangeRole: (changeRole: boolean) => void
};

const TieInfo = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    width: 100%;
    margin-bottom: 30px;
`;

const TieTitle = styled.div`
    font-size: 25px;
    font-weight: bold;
`;

const VersionTitle = styled.div`
    font-size: 20px;
    font-weight: bold;
    color: #1577AE;
`;

const LicenceTitle = styled.div`
    display: flex;
    flex-direction: column;
    font-size: 18px;
    font-weight: bold;
`;

const LicenceItem = styled.div`
`;

const LeftColumn = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    & > main {
        margin-bottom: 12px;
    }
`;

const RightColumn = styled.div`
    display: flex;
    gap: 15px;
    flex-direction: column;
    align-items: stretch;
    flex: 1 1 auto;
`;

const HorizontalContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    & > div,
    & > main {
        flex: 1 0 auto;
        margin: 0 12px;
    }
    & > div:nth-child(1),
    & > main:nth-child(1) {
        flex: 1 0 auto;
        margin-left: 0;
    }
    & > div:nth-last-child(1),
    & > main:nth-last-child(1) {
        flex: 1 0 auto;
        margin-right: 0;
    }
`;

const IntersystemsDashboard = (props: IProps) => {
    const { state, dispatch } = React.useContext(SessionContext);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState("");
    const [data, setData] = React.useState(undefined as unknown as IRhapsodyServer[]);
    const [dashboard, setDashboard] = React.useState(undefined as unknown as IRhapsodyDashboard);
    const [selectedNamespace, setSelectedNamespace] = useCookie<string>('selectedNamespace', '');
    const roleContext = React.useContext(RolesContext);
    const headerInfo = state.headerInfo ? state.headerInfo : data;
    const currentRole = roleContext.state.role;
    const selectedServer = state.selectedSever ?? 0;

    // Use ref to track the current token and avoid stale closures
    const currentTokenRef = React.useRef(state.webToken);
    const isMountedRef = React.useRef(true);

    React.useEffect(() => {
        currentTokenRef.current = state.webToken;
    }, [state.webToken]);

    const setSelectedServer = (index: number) => {
        dispatch({ type: "setSelectedServer", payload: index });
    };

    React.useEffect(() => {

        let interval: NodeJS.Timeout;
        let isActive = true;

        const fetchHeaderAndDashboard = () => {
            const token = currentTokenRef.current;
                        
            if (!isActive || !isMountedRef.current) {
                return;
            }

            setLoading(true);
            
            getRhapsodyHeader(token, (serverData: IRhapsodyServer[]) => {
                if (!isActive || !isMountedRef.current) {
                    return;
                }

                setData(serverData);
                setError("");
                dispatch({ type: "setHeaderInfo", payload: serverData });

                if (serverData[selectedServer]) {
                    getRhapsodyDashboard(serverData[selectedServer].id, token, (dashboardData: IRhapsodyDashboard) => {
                        if (!isActive || !isMountedRef.current) {
                            return;
                        }
                        setDashboard(dashboardData);
                        setError("");
                        setLoading(false);
                    }, (errorMessage: string) => {
                        if (!isActive || !isMountedRef.current) {
                            return;
                        }
                        setError(errorMessage);
                        setLoading(false);
                    });
                } else {
                    setLoading(false);
                }
            }, (errorMessage: string) => {
                if (!isActive || !isMountedRef.current) {
                    return;
                }
                setError(errorMessage);
                setLoading(false);
            }, selectedNamespace && selectedNamespace.trim() !== '' ? selectedNamespace : undefined);
        };

        fetchHeaderAndDashboard();
        
        interval = setInterval(() => {
            fetchHeaderAndDashboard();
        }, DASHBOARD_REFRESH_INTERVAL_MS);

        return () => {
            isActive = false;
            clearInterval(interval);
        };
    }, [state.webToken, currentRole, selectedServer, selectedNamespace, dispatch]);

    React.useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    return <ContentContainer>
        <LoadingIndicator type="Linear" show={loading} />
        {error && <ErrorBox>{error}</ErrorBox>}
        {headerInfo && headerInfo[selectedServer] && <TieInfo>
            <TieTitle>InterSystems Integration Engine</TieTitle>
            <VersionTitle>Version {headerInfo[selectedServer].licenceVersion}</VersionTitle>
            <LicenceTitle>
                <LicenceItem>Licence: {headerInfo[selectedServer].licenceEndUser}</LicenceItem>
                <LicenceItem>Expiry: {headerInfo ? getExpiryDate(headerInfo[selectedServer]) : ""}</LicenceItem>
            </LicenceTitle>
        </TieInfo>}
        <ServerSelector headerInfo={headerInfo} selectedNamespace={selectedNamespace}
            selectedServer={selectedServer} setSelectedNamespace={setSelectedNamespace} setSelectedServer={setSelectedServer}/>
        {!loading && <SiteContainer>
            <LeftColumn>
                {dashboard && <ServerDetails token={state.webToken} data={dashboard.server} interystems={true}/>}
                {dashboard && <DiskUsagePanel spaceMetrics={dashboard.server.spaceMetrics}/>}
            </LeftColumn>
            <RightColumn>
                {dashboard && <MessageThroughputPanel today={dashboard.todayMessageCounts} yesterday={dashboard.yesterdayMessageCounts} />}
                <HorizontalContainer>
                    {dashboard && <IntersystemsComponentPanel data={dashboard.intersystemsComponents} />}
                    {dashboard && <AlertsPanel data={dashboard.alerts} forwardTo="/Ram/Monitor Alerts" />}
                </HorizontalContainer>
            </RightColumn>
        </SiteContainer>}
    </ContentContainer>;
};

export default IntersystemsDashboard;